import { onBeforeUnmount, Ref, ref } from 'vue';
import { NotificationQuery } from '@/services/api/resources/notification/query';
import { Task, useTask } from '@/ui/concerns';
import { list } from '@/services/api/resources/notification';
import { sequence } from '@/ui/helpers';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';

type BaseEntity<T> = (T & { id: number });
interface UseNotificationCheckArgs<T> {
  getList: Task;
  bulkUpdateFn: Function;
  mapIds?: (value: T, index?: number, array?: T[]) => number;
  onUpdateFilterFn?: (deleteEntity: BaseEntity<T>) => ((currentElement: T) => boolean);
}

const defaultMapIds = <T>(record: BaseEntity<T>) => record.id;

export function useNotificationCheck<T extends { id: number }>({
  getList,
  bulkUpdateFn,
  mapIds = defaultMapIds,
}: UseNotificationCheckArgs<T>) {
  const checkLoading = ref<boolean>(true);
  const checkResult = ref<boolean>();
  const query = (): NotificationQuery => ({
    search: '',
    sort: '',
    dir: '',
    page: 1,
    perPage: 1,
    from: null,
    to: null,
  });

  const performCheck = useTask(
    () => sequence(
      list(query()),
    ),
    ([
      listResponse,
    ]) => {
      checkResult.value = listResponse.data.length > 0;
      checkLoading.value = false;
    },
  );

  const selectedResources = ref<T[]>([]) as Ref<T[]>;

  const updateBulkTask = () => {
    checkLoading.value = true;

    useTask(
      () => bulkUpdateFn(selectedResources.value.map(mapIds)),
      () => {
        selectedResources.value = [];
        checkLoading.value = false;
        getList.run();
      },
    ).run();
  };

  const onCheckedRowsUpdate = (resources: T[]) => {
    selectedResources.value = resources;
  };

  EventBus.$on(Events.bulkUpdateClick, updateBulkTask);

  onBeforeUnmount(() => {
    EventBus.$off(Events.bulkUpdateClick, updateBulkTask);
  });

  return {
    checkLoading,
    checkResult,
    performCheck,
    selectedResources,
    onCheckedRowsUpdate,
  };
}
